/**
 * frontend/src/components/ReporteFlotante.ts
 * ============================================
 * El botón de reportar un problema, presente en toda la aplicación.
 *
 * Por qué flota
 * -------------
 * Un problema aparece en cualquier pantalla y, casi siempre, en la peor: el
 * número no cuadra, el asistente se quedó, el resultado no es el esperado. Si
 * para reportarlo hay que salir de ahí, volver al inicio y buscar un menú, no
 * se reporta: se cierra la pestaña y se avisa por mensaje suelto, sin versión,
 * sin usuario y sin el archivo.
 *
 * Abajo a la derecha porque es donde lo busca cualquiera que haya usado una
 * aplicación web en los últimos diez años. No tapa la acción principal, que en
 * esta aplicación vive en el centro y en el pie del contenido.
 *
 * Tres formas de adjuntar
 * ------------------------
 * El botón, arrastrar los archivos y pegar con Ctrl+V. La tercera existe porque
 * `Impr Pant` deja la captura en el portapapeles y no en un archivo: sin
 * pegado, adjuntar una captura obliga a abrirla en algún programa y guardarla,
 * y esa vuelta es la que hace que no se mande.
 *
 * Qué manda además de lo que escribe el usuario
 * ---------------------------------------------
 * La pantalla donde estaba y la conciliación en curso, si había una. Son los
 * dos datos que nadie recuerda al describir un problema y sin los cuales
 * reproducirlo es adivinar.
 *
 * El consentimiento
 * -----------------
 * No es un trámite. El adjunto puede ser la captura de un extracto real o el
 * archivo entero de un cliente, y mandarlo saca esos datos del equipo del
 * usuario, que es justo lo que el resto de la aplicación evita. Por eso el
 * aviso está al lado del selector de archivo, el botón no se habilita sin la
 * marca, y el texto dice qué se envía y a dónde.
 */

import { ApiError, enviarReporte } from "../api";
import { escapeHtml as _esc } from "../utils/texto";
import { icon } from "../utils/icons";
import { prepararDialogo } from "../utils/ui";

/** Lo que el componente pregunta antes de abrir el formulario. */
export interface ContextoReporte {
  /** Nombre legible de la vista actual. */
  pantalla: string;
  /** Conciliación en curso, si hay una. */
  threadId: string | null;
}

const MAX_ADJUNTO_MB = 10;
const MAX_ADJUNTOS = 5;

const FORMATOS = ".png,.jpg,.jpeg,.webp,.gif,.pdf,.csv,.txt,.xlsx,.xls";

let boton: HTMLButtonElement | null = null;

/**
 * Coloca el botón en la página. Idempotente: llamarlo dos veces no duplica.
 *
 * @param contexto Se consulta al abrir, no al montar: la pantalla cambia
 *                 mientras el botón sigue ahí.
 */
export function montarBotonDeReporte(contexto: () => ContextoReporte): void {
  if (boton) return;

  boton = document.createElement("button");
  boton.type = "button";
  boton.className = "reporte-flotante";
  boton.id = "btn-reportar";
  boton.innerHTML = icon("bug");

  const ayuda = "Reportar un problema al equipo de soporte";
  boton.setAttribute("aria-label", ayuda);
  boton.setAttribute("data-tooltip", ayuda);

  boton.addEventListener("click", () => abrirFormulario(contexto()));
  document.body.appendChild(boton);
}

/** Saca el botón. Se usa al cerrar sesión: sin sesión no hay a dónde enviar. */
export function desmontarBotonDeReporte(): void {
  boton?.remove();
  boton = null;
}

function abrirFormulario(contexto: ContextoReporte): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "reporte-titulo");

  overlay.innerHTML = `
    <div class="modal-panel modal-panel--reporte">

      <div class="modal-header">
        <span class="modal-header__icon">${icon("bug")}</span>
        <h2 class="modal-header__title" id="reporte-titulo">Reportar un problema</h2>
        <button class="modal-close" type="button" aria-label="Cerrar">${icon("close")}</button>
      </div>

      <form class="modal-body reporte-form" id="reporte-form" novalidate>

        <label class="reporte-campo">
          <span class="reporte-campo__label">¿Qué ocurrió?</span>
          <input id="reporte-asunto" type="text" maxlength="120" required
                 placeholder="Ej: el total conciliado no coincide con el extracto" />
        </label>

        <label class="reporte-campo">
          <span class="reporte-campo__label">Detalle <em>(opcional)</em></span>
          <textarea id="reporte-comentario" rows="4" maxlength="2000"
                    placeholder="Qué esperaba que pasara, qué pasó, y en qué momento."></textarea>
        </label>

        <div class="reporte-campo">
          <span class="reporte-campo__label" id="reporte-adjunto-label">Adjuntos <em>(opcional)</em></span>
          <div class="reporte-archivo" id="reporte-zona">
            <input id="reporte-adjunto" type="file" multiple accept="${FORMATOS}"
                   aria-labelledby="reporte-adjunto-label" />
            <label class="reporte-archivo__boton" for="reporte-adjunto">
              ${icon("upload")} <span>Seleccionar archivos</span>
            </label>
            <span class="reporte-archivo__nombre" id="reporte-archivo-nombre">arrástrelos o pegue con Ctrl+V</span>
            <ul class="reporte-lista" id="reporte-lista" aria-live="polite"></ul>
          </div>
          <span class="reporte-campo__ayuda">
            Capturas de pantalla, informes o el archivo con el que estaba trabajando.
            Hasta ${MAX_ADJUNTOS} archivos, ${MAX_ADJUNTO_MB} MB cada uno.
          </span>
        </div>

        <label class="reporte-consentimiento" for="reporte-ok">
          <input id="reporte-ok" type="checkbox" />
          <span>
            Me hago responsable del envío de este reporte y de sus archivos adjuntos
            al soporte de Siete Ideas.
          </span>
        </label>

        <p class="reporte-estado" id="reporte-estado" role="status" aria-live="polite" hidden></p>
      </form>

      <div class="modal-footer">
        <button class="btn btn--ghost" type="button" id="reporte-cancelar">Cancelar</button>
        <button class="btn btn--primary" type="submit" form="reporte-form" id="reporte-enviar" disabled>
          <span>Enviar reporte</span>
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const form        = overlay.querySelector<HTMLFormElement>("#reporte-form")!;
  const asunto      = overlay.querySelector<HTMLInputElement>("#reporte-asunto")!;
  const comentario  = overlay.querySelector<HTMLTextAreaElement>("#reporte-comentario")!;
  const archivo     = overlay.querySelector<HTMLInputElement>("#reporte-adjunto")!;
  const autoriza    = overlay.querySelector<HTMLInputElement>("#reporte-ok")!;
  const estado      = overlay.querySelector<HTMLParagraphElement>("#reporte-estado")!;
  const enviar      = overlay.querySelector<HTMLButtonElement>("#reporte-enviar")!;
  const lista       = overlay.querySelector<HTMLUListElement>("#reporte-lista")!;

  // La lista vive acá y no en el `input`, porque el `input` solo refleja la
  // última elección: elegir dos archivos y después pegar una captura dejaría
  // solo la captura. Acá se suman, que es lo que el usuario espera al adjuntar
  // de tres formas distintas en el mismo formulario.
  //
  // Se muestran como miniaturas cuadradas en una fila, no como una lista
  // vertical: con cinco archivos, la lista empujaba el formulario hasta dejar
  // el botón de enviar fuera de la vista. Una fila crece a lo ancho y no a lo
  // alto.
  interface Adjunto {
    archivo: File;
    /** URL temporal de la imagen. Solo para lo que se puede previsualizar. */
    vista: string | null;
  }

  const adjuntos: Adjunto[] = [];

  const extensionDe = (nombre: string): string =>
    nombre.includes(".") ? nombre.split(".").pop()!.toUpperCase() : "?";

  const dibujarLista = (): void => {
    lista.innerHTML = adjuntos
      .map(({ archivo: f, vista }, i) => {
        const titulo = `${_esc(f.name)} · ${Math.max(1, Math.round(f.size / 1024))} KB`;
        const cuerpo = vista
          ? `<img src="${vista}" alt="" />`
          : `<span class="reporte-miniatura__ext">${_esc(extensionDe(f.name))}</span>`;
        return `
        <li class="reporte-miniatura" data-tooltip="${titulo}">
          ${cuerpo}
          <button class="reporte-miniatura__quitar" type="button" data-quitar="${i}"
                  aria-label="Quitar ${_esc(f.name)}">${icon("close")}</button>
        </li>`;
      })
      .join("");

    lista.querySelectorAll<HTMLButtonElement>("[data-quitar]").forEach((boton) => {
      boton.addEventListener("click", () => {
        const [quitado] = adjuntos.splice(Number(boton.dataset.quitar), 1);
        // La URL temporal ocupa memoria hasta que se libera a mano.
        if (quitado?.vista) URL.revokeObjectURL(quitado.vista);
        dibujarLista();
      });
    });
  };

  /** Suma archivos sin repetir, respetando el tope. Devuelve qué no entró. */
  const sumar = (nuevos: File[]): string => {
    for (const archivoNuevo of nuevos) {
      if (adjuntos.length >= MAX_ADJUNTOS) {
        dibujarLista();
        return `Puede adjuntar hasta ${MAX_ADJUNTOS} archivos.`;
      }
      // El mismo archivo elegido dos veces, o elegido y soltado, no tiene por
      // qué viajar dos veces. `lastModified` entra en la comparación porque
      // sin él dos capturas distintas del mismo tamaño —dos pantallas de la
      // misma resolución— se tomaban por la misma y la segunda se perdía en
      // silencio.
      const repetido = adjuntos.some(
        (a) => a.archivo.name === archivoNuevo.name
          && a.archivo.size === archivoNuevo.size
          && a.archivo.lastModified === archivoNuevo.lastModified
      );
      if (!repetido) {
        adjuntos.push({
          archivo: archivoNuevo,
          vista: archivoNuevo.type.startsWith("image/")
            ? URL.createObjectURL(archivoNuevo)
            : null,
        });
      }
    }
    dibujarLista();
    return "";
  };

  const avisarSiSobra = (mensaje: string): void => {
    if (mensaje) mostrar(mensaje, "error");
  };

  archivo.addEventListener("change", () => {
    avisarSiSobra(sumar(Array.from(archivo.files ?? [])));
    // El control se vacía: lo que vale es la lista, y si no se vaciara,
    // volver a elegir el mismo archivo no dispararía `change`.
    archivo.value = "";
  });

  // Arrastrar y soltar. Es el gesto natural para una captura de pantalla: se
  // toma, aparece en el escritorio y se suelta acá, sin pasar por el
  // explorador de archivos.
  //
  // `dragover` tiene que cancelarse en cada evento, no solo al entrar: sin eso
  // el navegador aplica su comportamiento propio, que es abrir el archivo en
  // una pestaña y perder el formulario a medio llenar.
  const zona = overlay.querySelector<HTMLElement>("#reporte-zona")!;

  const marcar = (activa: boolean) => zona.classList.toggle("reporte-archivo--soltar", activa);

  zona.addEventListener("dragover", (evento) => {
    evento.preventDefault();
    marcar(true);
  });
  zona.addEventListener("dragleave", () => marcar(false));
  zona.addEventListener("drop", (evento) => {
    evento.preventDefault();
    marcar(false);

    const soltados = Array.from(evento.dataTransfer?.files ?? []);
    if (soltados.length) avisarSiSobra(sumar(soltados));
  });

  // Pegar con Ctrl+V. En Windows, `Impr Pant` deja la captura en el
  // portapapeles y nunca llega a ser un archivo: para adjuntarla hay que
  // abrirla en algún programa y guardarla primero. Esto se salta ese rodeo,
  // que es el que hace que la captura no se mande.
  //
  // Escucha en todo el formulario y no solo en la zona, porque nadie enfoca la
  // zona antes de pegar: se pega donde esté el cursor, casi siempre en el
  // detalle. Si el portapapeles trae texto, no se toca nada y el pegado
  // normal sigue su curso.
  overlay.addEventListener("paste", (evento: ClipboardEvent) => {
    const imagen = Array.from(evento.clipboardData?.items ?? [])
      .find((item) => item.kind === "file" && item.type.startsWith("image/"));
    if (!imagen) return;

    const captura = imagen.getAsFile();
    if (!captura) return;

    evento.preventDefault();

    // El portapapeles no trae nombre: sin uno propio, todas las capturas
    // llegan a soporte como "image.png" y no hay forma de distinguirlas.
    const sello = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const extension = captura.type.split("/")[1] ?? "png";

    // Dos pegados dentro del mismo segundo daban el mismo nombre, y en el
    // correo dos adjuntos homónimos se pisan. El sufijo los separa.
    let nombre = `captura-${sello}.${extension}`;
    for (let n = 2; adjuntos.some((a) => a.archivo.name === nombre); n++) {
      nombre = `captura-${sello}-${n}.${extension}`;
    }

    avisarSiSobra(sumar([new File([captura], nombre, { type: captura.type })]));
  });

  const cerrar = (): void => {
    for (const { vista } of adjuntos) if (vista) URL.revokeObjectURL(vista);
    overlay.remove();
  };
  prepararDialogo(overlay, cerrar);
  overlay.querySelector(".modal-close")!.addEventListener("click", cerrar);
  overlay.querySelector("#reporte-cancelar")!.addEventListener("click", cerrar);

  const mostrar = (mensaje: string, tono: "error" | "exito"): void => {
    estado.className = `reporte-estado reporte-estado--${tono}`;
    estado.innerHTML = `${icon(tono === "error" ? "alert" : "check")} <span>${_esc(mensaje)}</span>`;
    estado.hidden = false;
  };

  // El botón solo se habilita con las dos condiciones que el servidor exige.
  // Deshabilitado y sin explicación es peor que no tenerlo: se pulsa, no pasa
  // nada, y nadie sabe qué falta.
  const revisar = (): void => {
    const falta = !asunto.value.trim()
      ? "Escriba qué ocurrió."
      : !autoriza.checked
        ? "Marque la autorización para poder enviarlo."
        : "";
    enviar.disabled = falta !== "";
    enviar.setAttribute("data-tooltip", falta);
    if (!falta) enviar.removeAttribute("data-tooltip");
  };

  asunto.addEventListener("input", revisar);
  autoriza.addEventListener("change", revisar);
  revisar();
  asunto.focus();

  form.addEventListener("submit", async (evento) => {
    evento.preventDefault();

    const pesado = adjuntos
      .map((a) => a.archivo)
      .find((f) => f.size > MAX_ADJUNTO_MB * 1024 * 1024);
    if (pesado) {
      mostrar(
        `«${pesado.name}» pesa ${Math.round(pesado.size / (1024 * 1024))} MB y el ` +
        `máximo por archivo son ${MAX_ADJUNTO_MB} MB. Si es una captura, recórtela.`,
        "error"
      );
      return;
    }

    enviar.disabled = true;
    enviar.querySelector("span")!.textContent = "Enviando…";
    estado.hidden = true;

    try {
      const { referencia } = await enviarReporte({
        asunto: asunto.value.trim(),
        comentario: comentario.value.trim(),
        pantalla: contexto.pantalla,
        threadId: contexto.threadId,
        adjuntos: adjuntos.map((a) => a.archivo),
        consentimiento: autoriza.checked,
      });

      // La referencia queda a la vista unos segundos antes de cerrar: es lo
      // único que el usuario puede mencionar si después reclama por esto.
      mostrar(`Reporte enviado. Su referencia es ${referencia}.`, "exito");
      form.querySelectorAll("input, textarea").forEach((c) => {
        (c as HTMLInputElement).disabled = true;
      });
      enviar.querySelector("span")!.textContent = "Enviado";
      setTimeout(cerrar, 4000);
    } catch (err) {
      mostrar(
        err instanceof ApiError ? err.message : "No se pudo enviar el reporte.",
        "error"
      );
      enviar.disabled = false;
      enviar.querySelector("span")!.textContent = "Enviar reporte";
    }
  });
}
